import type { ColumnProfile, Dependency, ImportSpec, SheetRow, SpecIdentifier } from '@/lib/api';
import type { OntologySchema } from '@/lib/useOntology';
import {
  isThingColumn,
  mappingProblems,
  mappingsToColumns,
  mappingsToNewClasses,
  mintedPredicate,
  type ColumnMapping,
  type MappingProblem,
  type RelationshipDraft,
} from '@/lib/columnMapping';

/**
 * Several sheets through the three pages (ruled 2026-09-30, decision 4.11;
 * manual Ch13 section 2.0.2). Page 1 is one Columns page over every sheet;
 * page 2 is the links between sheets; the properties, the rules, Validate
 * and Commit run over all of them as for one sheet. This module holds the
 * sheets' state and lowers the links into the sheets' specs.
 */

/** One sheet of the folder as the pages hold it. */
export interface SheetState {
  /** The staged file and the sheet in it, as the folder plan names them. */
  file: string;
  sheet: string;
  /** The row the header is on, as the plan read it; sent with every preview
   *  and commit of the sheet. */
  headerRow: number;
  columns: string[];
  /** The first rows only, as the plan carries them; the whole sheet stays
   *  staged on the server. */
  rows: SheetRow[];
  profiles: Map<string, ColumnProfile>;
  /** Which column's values determine another's, from the sheet's profile. */
  dependencies: Dependency[];
  mappings: ColumnMapping[];
  /** The spec as the mapping lowers it, kept in step with `mappings`. */
  spec: ImportSpec;
  draft: RelationshipDraft;
  /** Left out of the import, with the reason. */
  leftOut?: string;
  /** The saved mapping the sheet's answers came from, or were last saved to. */
  savedMapping?: { id: string; name: string } | undefined;
  /** The loads in force that this sheet is a new version of, newest first. */
  earlier?: { source: string; imported_at: string; rows: number | null }[] | undefined;
  /** For a new version: add it and keep both, or replace the earlier one. */
  version?: 'add' | 'replace' | undefined;
}

/** Which sheet a column is in: `file` and `sheet` as the plan names them. */
export interface SheetColumnRef {
  sheet: string;
  column: string;
}

export const LINK_KINDS = ['same_identifier', 'same_entity', 'object_property', 'subclass', 'context'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

export const LINK_WORDS: Record<LinkKind, string> = {
  same_identifier: 'Same identifier',
  same_entity: 'Same entity, different identifiers',
  object_property: 'ObjectProperty',
  subclass: 'Subclass',
  context: 'Context',
};

/** A row of the links page. `from` is the domain side, `to` the range side.
 *  A row the report suggested is `accepted: false` until pressed, as a
 *  column's suggestion is; a row the person adds is accepted as made. */
export interface FolderLink {
  kind: LinkKind;
  from: SheetColumnRef;
  to: SheetColumnRef;
  /** ObjectProperty and context: the property's name. */
  predicate?: string;
  /** Same entity: which side is the primary identifier, the join key. */
  primary?: 'from' | 'to';
  /** ObjectProperty and context across sheets: the column of the `from`
   *  sheet that holds the `to` sheet's primary identifier, through which a
   *  row of the one names the entity of the other. */
  via?: string;
  /** The sentence the report gives for the row, shown beside Accept. */
  reason?: string;
  accepted: boolean;
}

export const sheetKey = (s: { file: string; sheet: string }) => `${s.file}::${s.sheet}`;

/** How a sheet is named on the pages: the file, and the tab when the file
 *  has several. */
export const sheetName = (s: { file: string; sheet: string }) => (s.sheet === 'Sheet1' ? s.file : `${s.file} / ${s.sheet}`);

export function sheetOf(sheets: SheetState[], key: string): SheetState | undefined {
  return sheets.find((s) => sheetKey(s) === key);
}

export function entityColumns(s: SheetState): string[] {
  return s.mappings.filter(isThingColumn).map((m) => m.column);
}

export function classOf(s: SheetState | undefined, column: string): string {
  return s?.mappings.find((m) => m.column === column && isThingColumn(m))?.klass.trim() || '?';
}

/** The identifier name a column goes by: its header in lower snake case. */
export function identifierName(column: string): string {
  return mintedPredicate(column).slice(1);
}

/** Columns of different sheets that already name one entity with nothing
 *  declared: the same class, the identity made the same way (the identifier
 *  as written, or a UUID minted from the label). The links page states these;
 *  they hold whether or not a row is added for them. */
export function sharedIdentifiers(sheets: SheetState[]): { klass: string; columns: SheetColumnRef[] }[] {
  const groups = new Map<string, SheetColumnRef[]>();
  for (const s of sheets) {
    if (s.leftOut) continue;
    for (const m of s.mappings) {
      const klass = m.klass.trim();
      if (!isThingColumn(m) || m.job !== 'id' || !klass || (m.idStrategy !== 'from_column' && m.idStrategy !== 'generated')) continue;
      const key = `${klass}\u0000${m.idStrategy}`;
      groups.set(key, [...(groups.get(key) ?? []), { sheet: sheetKey(s), column: m.column }]);
    }
  }
  return [...groups.entries()]
    .filter(([, columns]) => new Set(columns.map((c) => c.sheet)).size > 1)
    .map(([key, columns]) => ({ klass: key.split('\u0000')[0] ?? '', columns }));
}

/** What a link row still needs before it holds, in the order it is read.
 *  Empty when it is complete. */
export function linkProblems(link: FolderLink, sheets: SheetState[]): string[] {
  const out: string[] = [];
  const from = sheetOf(sheets, link.from.sheet);
  const to = sheetOf(sheets, link.to.sheet);
  const name = (key: string, s: SheetState | undefined) => (s ? sheetName(s) : key);
  // An end not yet picked is the picker's to name; only a picked column that
  // is not an Entity column is a problem of the link.
  if (link.from.column && (!from || !entityColumns(from).includes(link.from.column))) out.push(`${link.from.column} is not an Entity column of ${name(link.from.sheet, from)}.`);
  if (link.to.column && (!to || !entityColumns(to).includes(link.to.column))) out.push(`${link.to.column} is not an Entity column of ${name(link.to.sheet, to)}.`);
  if (link.kind === 'object_property' || link.kind === 'context') {
    if (link.kind === 'object_property' && !link.predicate?.trim()) out.push('Name the property.');
    if (link.from.sheet !== link.to.sheet) {
      if (!link.via) out.push(`Choose the column of ${name(link.from.sheet, from)} that holds the identifier of ${link.to.column}.`);
      else if (!from || !from.columns.includes(link.via)) out.push(`${link.via} is not a column of ${name(link.from.sheet, from)}.`);
    }
  }
  if (link.kind === 'same_identifier' || link.kind === 'same_entity') {
    if (from && to && classOf(from, link.from.column) !== classOf(to, link.to.column)) {
      out.push(`${link.from.column} and ${link.to.column} hold different classes; one identifier names one class.`);
    }
  }
  return out;
}

/** A declared object property that fits a link's two classes. `reversed`:
 *  it runs from the link's range to its domain, so using it swaps the ends. */
export interface DeclaredLink {
  uri: string;
  label: string | null;
  reversed: boolean;
}

/** The declared object properties whose domain and range fit two classes,
 *  either way round, a class fitting through any class above it. A property
 *  declared without a domain or a range fits nothing: it names no pair. */
export function declaredLinks(schema: OntologySchema | null, fromClass: string, toClass: string): DeclaredLink[] {
  if (!schema || !fromClass || !toClass) return [];
  const parents = new Map(schema.classes.map((c) => [c.uri, c.super_classes]));
  const lineage = (klass: string) => {
    const seen = new Set<string>();
    const walk = (c: string) => {
      if (seen.has(c)) return;
      seen.add(c);
      for (const p of parents.get(c) ?? []) walk(p);
    };
    walk(klass);
    return seen;
  };
  const from = lineage(fromClass);
  const to = lineage(toClass);
  const fits = (ends: string[], line: Set<string>) => ends.some((e) => line.has(e));
  const out: DeclaredLink[] = [];
  for (const p of schema.properties) {
    if (p.kind !== 'object') continue;
    if (fits(p.domain, from) && fits(p.range, to)) out.push({ uri: p.uri, label: p.label, reversed: false });
    else if (fits(p.domain, to) && fits(p.range, from)) out.push({ uri: p.uri, label: p.label, reversed: true });
  }
  return out.sort((a, b) => Number(a.reversed) - Number(b.reversed) || a.uri.localeCompare(b.uri));
}

/** The statement a link writes, in the ontology's terms. */
export function linkStatement(link: FolderLink, sheets: SheetState[]): string {
  const from = sheetOf(sheets, link.from.sheet);
  const to = sheetOf(sheets, link.to.sheet);
  const a = classOf(from, link.from.column);
  const b = classOf(to, link.to.column);
  switch (link.kind) {
    case 'same_identifier':
      return `${link.from.column} and ${link.to.column} are one ${a} in any order`;
    case 'same_entity':
      return `one ${a} under ${identifierName(link.from.column)} and ${identifierName(link.to.column)}; primary ${
        link.primary === 'to' ? identifierName(link.to.column) : identifierName(link.from.column)
      }`;
    case 'object_property':
      return `${link.predicate || '?'} :type :ObjectProperty ; :domain ${a} ; :range ${b}`;
    case 'subclass':
      return `${a} :subClassOf ${b}`;
    case 'context':
      return `the statements of ${from ? sheetName(from) : link.from.sheet} hold @context=${b}`;
  }
}

/** Every sheet's spec with the accepted links lowered into it. A same-entity
 *  link records both identifiers on the thing (ImportSpec.identifiers, 61.54)
 *  so the store finds it under either; an ObjectProperty or context across
 *  sheets goes through the `via` column, which holds the other sheet's
 *  entities and is an Entity column of their class; a subclass link puts the
 *  parent on the class the mapping created. A same-identifier link declares
 *  nothing: the same identifier under one class is one entity already. */
export function lowerSheets(sheets: SheetState[], links: FolderLink[]): { sheet: SheetState; spec: ImportSpec }[] {
  const accepted = links.filter((l) => l.accepted && linkProblems(l, sheets).length === 0);
  return sheets
    .filter((s) => !s.leftOut)
    .map((s) => {
      const key = sheetKey(s);
      let mappings = s.mappings;
      const identifiers: SpecIdentifier[] = [];
      for (const l of accepted) {
        if (l.kind === 'same_entity') {
          for (const side of [l.from, l.to]) {
            if (side.sheet === key) identifiers.push({ column: side.column, of_column: side.column, name: identifierName(side.column) });
          }
          const other = l.from.sheet === key ? l.to : l.from;
          if (other.sheet !== key && [l.from, l.to].some((x) => x.sheet === key)) {
            const own = l.from.sheet === key ? l.from : l.to;
            identifiers.push({ column: own.column, of_column: own.column, name: identifierName(other.column) });
          }
        }
        if ((l.kind === 'object_property' || l.kind === 'context') && l.from.sheet === key) {
          const viaColumn = l.from.sheet === l.to.sheet ? l.to.column : l.via;
          if (!viaColumn) continue;
          mappings = mappings.map((m) => {
            if (m.column !== l.from.column) return m;
            if (l.kind === 'object_property') {
              const predicate = l.predicate?.trim() || mintedPredicate(viaColumn);
              if (m.links.some((x) => x.predicate === predicate && x.to === viaColumn)) return m;
              return { ...m, links: [...m.links, { predicate, to: viaColumn, context: '' }] };
            }
            return m;
          });
          if (l.kind === 'context') {
            mappings = mappings.map((m) => (m.onColumn === l.from.column && !m.context ? { ...m, context: viaColumn } : m));
          }
        }
        if (l.kind === 'subclass' && l.from.sheet === key) {
          const parent = classOf(sheetOf(sheets, l.to.sheet), l.to.column);
          mappings = mappings.map((m) =>
            m.column === l.from.column && m.klassParent.trim() ? { ...m, klassParent: parent } : m,
          );
        }
      }
      const seen = new Set<string>();
      const distinct = identifiers.filter((i) => {
        const k = `${i.column}|${i.name}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      return {
        sheet: s,
        spec: {
          ...s.spec,
          columns: mappingsToColumns(mappings, s.spec.columns),
          new_classes: mappingsToNewClasses(mappings),
          ...(distinct.length ? { identifiers: distinct } : {}),
        },
      };
    });
}

/** A link saved between two sheets, as the links page holds it: accepted,
 *  since a person drew it and saved it. A kind this build does not know is
 *  left out. */
export function linkFromSaved(l: { kind: string; from: { sheet: { file: string; sheet: string }; column: string }; to: { sheet: { file: string; sheet: string }; column: string }; predicate?: string; primary?: string; via?: string }): FolderLink | null {
  if (!(LINK_KINDS as readonly string[]).includes(l.kind)) return null;
  return {
    kind: l.kind as LinkKind,
    from: { sheet: sheetKey(l.from.sheet), column: l.from.column },
    to: { sheet: sheetKey(l.to.sheet), column: l.to.column },
    ...(l.predicate ? { predicate: l.predicate } : {}),
    ...(l.primary === 'from' || l.primary === 'to' ? { primary: l.primary } : {}),
    ...(l.via ? { via: l.via } : {}),
    accepted: true,
  };
}

/** Every problem across the sheets, each naming its sheet. */
export function folderProblems(sheets: SheetState[], links: FolderLink[]): { sheet: string | null; problem: MappingProblem | { column: null; text: string } }[] {
  const out: { sheet: string | null; problem: MappingProblem | { column: null; text: string } }[] = [];
  for (const s of sheets) {
    if (s.leftOut) continue;
    for (const p of mappingProblems(s.mappings)) out.push({ sheet: sheetKey(s), problem: p });
  }
  for (const l of links) {
    if (!l.accepted) continue;
    for (const text of linkProblems(l, sheets)) out.push({ sheet: null, problem: { column: null, text } });
  }
  return out;
}
